// `detectMysqlCursorNameSlot` — classifies the procedural slot at the
// cursor so the local-variable resolver can emit declared cursor names at
// OPEN / FETCH / CLOSE positions (and nowhere else).
//
// Pinned by goals/autocomplete-coverage/expected/mysql.ts — script.46
// §6/§7: `c_top_cust`, `c_top_inv` are declared cursors that the spec
// expects at OPEN target / FETCH source positions, but the current
// resolver emits no cursor identifiers (cursors are not assignable
// variables, so the variable-emitting code path skips them).

import { tokenize } from '../context/tokenizer';

/** Cursor-name slot kinds. */
export type MysqlCursorNameSlot = 'open' | 'fetch' | 'close';

/**
 * Returns the cursor-name slot the cursor sits in, or null otherwise.
 *
 * Algorithm: tokenize from buffer start to cursor, walk forward; the slot
 * activates on the first `OPEN` / `FETCH` / `CLOSE` keyword inside the
 * current statement and stays active until either (a) the cursor identifier
 * is consumed, or (b) a slot-breaking token appears.
 *
 * For FETCH specifically: the cursor name is between `FETCH` and either
 * `INTO`, `FROM`, or `NEXT/PRIOR/...` cursor modifiers. We treat the slot
 * as active until the first `INTO`/`FROM` keyword (whichever closes the
 * cursor-name position).
 *
 * Statement boundaries: `;`, block-openers (`BEGIN`/`THEN`/`ELSE`/`END`/
 * `LOOP`/`DO`) reset the slot.
 */
export function detectMysqlCursorNameSlot(
  source: string,
  cursorOffset: number,
): MysqlCursorNameSlot | null {
  const tokens = tokenize(source, 0, cursorOffset, 'mysql');
  if (tokens.length === 0) return null;

  type SlotState =
    | { kind: 'none' }
    // After `OPEN`, expect a cursor identifier. The slot stays active
    // until `;`.
    | { kind: 'open-target' }
    // After `FETCH`, expect a cursor identifier. The slot stays active
    // until `INTO`/`FROM` (closes the name position) or `;`.
    | { kind: 'fetch-source' }
    // After `CLOSE`, expect a cursor identifier. The slot stays active
    // until `;`.
    | { kind: 'close-target' }
    // Inside the rest of the statement (after INTO/FROM closed the name
    // position, etc.).
    | { kind: 'consumed' };

  let state: SlotState = { kind: 'none' };

  const resetStatement = () => {
    state = { kind: 'none' };
  };

  for (const tok of tokens) {
    if (tok.start >= cursorOffset) break;

    if (tok.type === 'semicolon') {
      resetStatement();
      continue;
    }
    if (tok.type === 'kw' || tok.type === 'ident') {
      const upper = tok.text.toUpperCase();
      if (
        upper === 'BEGIN' ||
        upper === 'THEN' ||
        upper === 'ELSE' ||
        upper === 'END' ||
        upper === 'LOOP' ||
        upper === 'DO'
      ) {
        resetStatement();
        continue;
      }
      switch (state.kind) {
        case 'none':
          if (upper === 'OPEN') {
            state = { kind: 'open-target' };
          } else if (upper === 'FETCH') {
            state = { kind: 'fetch-source' };
          } else if (upper === 'CLOSE') {
            state = { kind: 'close-target' };
          }
          break;
        case 'fetch-source':
          // FETCH cursor INTO ... — once we see INTO/FROM, the cursor
          // name has been consumed and we're past the slot.
          if (upper === 'INTO' || upper === 'FROM') {
            state = { kind: 'consumed' };
          }
          break;
        default:
          break;
      }
    }
  }

  switch (state.kind) {
    case 'open-target':  return 'open';
    case 'fetch-source': return 'fetch';
    case 'close-target': return 'close';
    default: return null;
  }
}
